const MAX_RETRIES = 3
// One attempt only during a server render: retries multiply the 10s transport
// deadline into the render's own budget (3 × 10s + delays for a single call,
// two of them in parallel), and a visitor waiting on HTML is better served by
// the skeleton plus a client refetch than by a page that arrives 30s late
// (VIP-12038).
const SSR_MAX_RETRIES = 1
const _defaultMaxRetries = () =>
  import.meta.env.SSR ? SSR_MAX_RETRIES : MAX_RETRIES
const DELAY = 1000
const STATUS_REGEX = /^(408|429|5\d{2})$/
const DESCRIPTION_REGEX = /(Failed to fetch|Load failed|NetworkError)/
// Node's transient connection failures, the SSR counterpart of the browser
// fetch messages above (VIP-12038). Deliberately without ETIMEDOUT-style idle
// timeouts — the SSR transport words its timeout error to never retry.
const NODE_NETWORK_CODE_REGEX =
  /^(ECONNREFUSED|ECONNRESET|EPIPE|EAI_AGAIN|ENOTFOUND)$/
export const API_V2_REGEX = /api\.vicampo(-[a-z]+)?\.[^\s/]+\/v2/

const DISABLED_EXTRACTION = /\/(view|helpful\/vote)$/

class RequestHelper {
  static async sendGetRequest(url) {
    try {
      return await _fetchWithRetry(url, _getBaseConfig('GET'))
    } catch (e) {
      _handleError(e)
    }
  }

  static async sendPostRequest(url, data) {
    const config = _getBaseConfig('POST')
    try {
      return await _fetchWithRetry(
        url,
        { ...config, body: JSON.stringify(data) },
        1
      )
    } catch (e) {
      _handleError(e)
    }
  }

  /**
   * @param {string} url
   * @param {FormData} formData - Sent as multipart/form-data. The Content-Type is left
   *   to the browser on purpose: it has to carry the boundary, which we cannot know.
   *   Not retried - a repeated upload would store the file twice.
   */
  static async sendPostFormRequest(url, formData) {
    const config = _getBaseConfig('POST')
    delete config.headers['Content-Type']
    try {
      return await _fetchWithRetry(url, { ...config, body: formData }, 1)
    } catch (e) {
      _handleError(e)
    }
  }

  static async sendPatchRequest(url, data) {
    const config = _getBaseConfig('PATCH')
    try {
      return await _fetchWithRetry(url, {
        ...config,
        body: JSON.stringify(data)
      })
    } catch (e) {
      _handleError(e)
    }
  }

  static async sendPutRequest(url, data) {
    const config = _getBaseConfig('PUT')
    try {
      return await _fetchWithRetry(url, {
        ...config,
        body: JSON.stringify(data)
      })
    } catch (e) {
      _handleError(e)
    }
  }

  /**
   * @param {string} url
   * @param {object} [data] - Optional payload. Most DELETE endpoints identify the
   *   resource by path and need none; account deletion has to carry the current
   *   password and the confirmation, which must not travel in the URL. Sending it
   *   requires the JSON content type, since that is what the API parses a body on.
   */
  static async sendDeleteRequest(url, data) {
    const hasBody = undefined !== data
    const config = _getBaseConfig('DELETE', hasBody)
    try {
      return await _fetchWithRetry(
        url,
        hasBody ? { ...config, body: JSON.stringify(data) } : config
      )
    } catch (e) {
      _handleError(e)
    }
  }
}

const withoutHeader = /(GET|DELETE)/

let _requestPathResolver = null

/**
 * Installed by the server entry: returns the rendering request's path (or
 * undefined outside a request) as the server-side stand-in for
 * `location.pathname`. A resolver rather than a stored value so concurrent
 * renders cannot read each other's URL (VIP-12038).
 *
 * @param {() => String | undefined} resolver
 */
export const setRequestPathResolver = (resolver) => {
  _requestPathResolver = resolver
}

/**
 * The browser URL path — `location.pathname`, or the rendering request's
 * route path during SSR where `location` does not exist. Exposed for callers
 * that put the path into request payloads (e.g. the PDP fetches' product_url
 * redirect parameter).
 *
 * @return {String}
 */
export const getRequestPathname = () =>
  import.meta.env.SSR ? (_requestPathResolver?.() ?? '') : location.pathname

// The Pathname header carries the browser URL for backend logging
const _getPathname = () => getRequestPathname()

const _getBaseConfig = (method, withBody = false) => {
  if (!withBody && withoutHeader.test(method)) {
    return {
      method,
      credentials: 'include',
      headers: { Pathname: _getPathname() }
    }
  }
  return {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Pathname: _getPathname()
    }
  }
}

const _extractResponse = async (response) => {
  const { status, headers: originalHeaders, ok, url } = response

  // prevent error if tracking is disabled on the fly (analytics intercepts the request and fails it)
  if (DISABLED_EXTRACTION.test(url)) {
    return { headers: {}, data: {} }
  }
  if (!ok) {
    // Gateway errors (ALB/nginx 5xx pages) carry HTML bodies — a parse
    // failure must not mask the HTTP status, or the 5xx retry and the error
    // shape below are lost (VIP-12038)
    const data = await response.json().catch(() => undefined)
    // Support both { error: {...} } and v2-style { data: { error: {...} } }
    const errorData = data?.error ?? data?.data?.error

    const cause = errorData?.vicampoErrorCode
    const description = errorData?.description

    const error = new Error(
      `[${url}] HTTP-${status} – description: ${description}`,
      { cause }
    )

    error.status = status
    error.cause = cause
    error.description = description
    error.errorData = errorData
    error.field = errorData?.field
    error.fields = errorData?.fields
    error.address_type = errorData?.address_type

    throw error
  }

  const headers = _getAsPlainObject(originalHeaders)

  if (status < 200 || status > 202) return { headers, data: {} }
  const data = await _extractDataField(response)

  return { headers, data }
}

const _handleError = (originalError) => {
  if (!originalError || typeof originalError !== 'object') {
    throw originalError
  }

  const {
    message,
    status,
    cause,
    description,
    stack,
    errorData,
    field,
    fields,
    address_type
  } = originalError
  let errorMessage

  try {
    errorMessage = JSON.stringify({ status, description: message })
  } catch (stringifyError) {
    console.error('Failed to stringify error details:', stringifyError)
    errorMessage = `[HTTP-${status}] Error: ${message}`
  }

  const e = new Error(errorMessage)

  e.status = status
  e.cause = cause
  e.description = description
  e.originalError = originalError
  e.errorData = errorData
  e.field = field
  e.fields = fields
  e.address_type = address_type
  if (stack) {
    e.stack = stack
  }

  throw e
}

const _extractDataField = async (response) => {
  try {
    const payload = await response.json()
    return API_V2_REGEX.test(response.url) ? payload.data : payload
  } catch (e) {
    _prefixUrl(e, response.url)
    throw e
  }
}

// Bare fetch() network failures ("Failed to fetch", "Load failed") carry no
// URL; add it so error logs are actionable (VIP-12034)
const _prefixUrl = (error, url) => {
  if (error?.urlPrefixed) return
  if (typeof error?.message !== 'string' || error.message.includes(url)) return
  try {
    error.message = `[${url}] ${error.message}`
    error.urlPrefixed = true
  } catch {
    // message is getter-only on some errors (e.g. DOMException); leave as-is
  }
}

// On the server, API-root URLs are re-targeted VPC-internally with the
// per-site Host header (VIP-12038). The SSR flag is a build-time constant in
// the app bundles, so the client build drops the branch and never pulls the
// node:http-based transport in.
const _doFetch = async (url, options) => {
  if (import.meta.env.SSR) {
    const { ssrFetch } = await import('@/services/SsrApiTransport')
    return ssrFetch(url, options)
  }
  return fetch(url, options)
}

const _fetchWithRetry = async (
  url,
  options,
  maxRetries = _defaultMaxRetries(),
  attempt = 1
) => {
  try {
    return await _extractResponse(await _doFetch(url, options))
  } catch (e) {
    const retry = attempt < maxRetries && _shouldRetry(e)

    if (!retry) {
      if (e?.status === undefined) _prefixUrl(e, url)
      throw e
    }
    // Request failed, retrying
    await new Promise((resolve) => setTimeout(resolve, DELAY))
    return await _fetchWithRetry(url, options, maxRetries, attempt + 1)
  }
}

const _shouldRetry = ({ description, status, message, code }) => {
  return (
    STATUS_REGEX.test(status) ||
    DESCRIPTION_REGEX.test(description) ||
    (message && DESCRIPTION_REGEX.test(message)) ||
    (code && NODE_NETWORK_CODE_REGEX.test(code))
  )
}

const _getAsPlainObject = (headers) =>
  Array.from(headers.entries()).reduce(
    (collected, [key, value]) => ({ ...collected, [key]: value }),
    {}
  )

export default RequestHelper
